import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import { useMemo, useState } from 'react';
import Switch from 'components/UI/Switch';
import { PHONE_COUNTRY_CODE } from 'constants/global';
import ImgFlagMexico from 'assets/icons/flag-mex.svg';
import { GENDERS } from 'constants/registration';
import { GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import Modal from 'components/Modal';
import Select from 'components/UI/Select';
import EmbedPdf from 'components/EmbedPdf';
import { getCMSDocumentSrc } from 'utils/getCMSDocumentSrc';
import useConfiguration from 'hooks/useConfiguration';
import { CMS_CONFIGURATION_KEY } from 'constants/cms';
import { isValidEmail } from 'utils/isValidEmail';
import StepHeader from './StepHeader';

const TERMS = {
  APP: 'app',
  PRIVACY: 'privacy',
};

const ClientBasicInfo = ({
  literals,
  user,
  onChange,
  onContinue,
  onBack,
  onEvent,
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const { getConfigurationValue } = useConfiguration();
  const privacyPolicyTerms = getConfigurationValue(CMS_CONFIGURATION_KEY.REGISTRATION_POLICY_TERMS, true);
  const appTerms = getConfigurationValue(CMS_CONFIGURATION_KEY.REGISTRATION_APP_TERMS, true);

  const disabled = useMemo(() => {
    return !user.firstName || !user.paternal || !user.maternal || !user.email || !isValidEmail(user.email) || user.phone?.length !== 10 || !user.gender || !user.privacyTerms || !user.appTerms;
  }, [user]);

  const handleChangeInputName = (field, value) => {
    const auxValue = (value || '').toUpperCase().replace(/[^A-Z\s]/g, '');
    onChange({ ...user, [field]: auxValue });
  };

  const handleContinue = () => {
    onEvent(
      GA_REGISTRATION_ACTIONS.EVENT,
      GA_REGISTRATION_LABELS.BASIC_INFO_SUBMIT,
    );
    onContinue(user);
  };

  return (
    <div className='full-screen-container'>
      <div className='screen-content'>
        <StepHeader step={2} onEvent={onEvent} />
        <p className='font-bold text-primary mb-4 text-xl'>{literals.basicInfo.letsStart}</p>
        <p className='font-black mb-6'>{literals.basicInfo.personalData}</p>
        <p className='mb-2'>{literals.basicInfo.yourFullName}</p>
        <Input
          className='mb-2'
          placeholder={literals.basicInfo.firstName}
          autoComplete='given-name'
          value={user.firstName}
          onChange={(firstName) => handleChangeInputName('firstName', firstName)}
          required
        />
        <Input
          className='mb-2'
          placeholder={literals.basicInfo.lastName}
          autoComplete='additional-name'
          value={user.lastName}
          onChange={(lastName) => handleChangeInputName('lastName', lastName)}
        />
        <Input
          className='mb-2'
          placeholder={literals.basicInfo.paternal}
          autoComplete='family-name'
          value={user.paternal}
          onChange={(paternal) => handleChangeInputName('paternal', paternal)}
          required
        />
        <Input
          className='mb-2'
          placeholder={literals.basicInfo.maternal}
          value={user.maternal}
          onChange={(maternal) => handleChangeInputName('maternal', maternal)}
          required
        />
        <Select
          className='mb-2'
          options={[{
            label: literals.basicInfo.male,
            value: GENDERS.MALE,
          }, {
            label: literals.basicInfo.female,
            value: GENDERS.FEMALE,
          }]}
          value={user.gender}
          placeholder={literals.basicInfo.gender}
          onChange={(gender) => onChange({ ...user, gender })}
          canBeEmpty={false}
          required
        />
        <p className='mb-2 mt-6'>{literals.basicInfo.yourEmail}</p>
        <Input
          className='mb-2'
          placeholder={literals.basicInfo.email}
          autoComplete='email'
          value={user.email}
          onChange={(email) => onChange({ ...user, email: (email || '').toLowerCase() })}
          required
        />
        <p className='mb-2 mt-6'>{literals.basicInfo.yourPhone}</p>
        <div className='phone-prefix-container mb-2'>
          <div className='phone-prefix'>
            <img width={25} src={ImgFlagMexico} alt='Mexico' />
            <p className='font-bold mb-0'>{PHONE_COUNTRY_CODE}</p>
          </div>
          <Input
            className='mb-0'
            placeholder={literals.basicInfo.phone}
            autoComplete='phone'
            type='tel'
            value={user.phone}
            onChange={(phone) => onChange({ ...user, phone })}
            required
            prefix={false}
          />
        </div>
        <div className='flex items-center justify-between gap-2 mt-6 mb-2'>
          <p className={`text-sm m-0 ${privacyPolicyTerms ? 'text-primary underline' : ''}`} onClick={() => privacyPolicyTerms && setShowTerms(TERMS.PRIVACY)}>
            {literals.basicInfo.acceptPrivacy}
          </p>
          <Switch
            checked={user.privacyTerms}
            onChange={(e) => onChange({ ...user, privacyTerms: e.target.checked })}
          />
        </div>
        <div className='flex items-center justify-between gap-6 mb-6'>
          <p className={`text-sm m-0 ${appTerms ? 'text-primary underline' : ''}`} onClick={() => appTerms && setShowTerms(TERMS.APP)}>
            {literals.basicInfo.acceptApp}
          </p>
          <Switch
            checked={user.appTerms}
            onChange={(e) => onChange({ ...user, appTerms: e.target.checked })}
          />
        </div>
      </div>
      <div className='screen-actions'>
        <Button onClick={handleContinue} disabled={disabled}>{literals.common.next}</Button>
        <Button type='outline-primary' onClick={onBack}>{literals.common.back}</Button>
      </div>
      {showTerms && (
        <Modal title={showTerms === TERMS.APP ? literals.basicInfo.termsAndConditions : literals.basicInfo.privacyPolicy} onClose={() => setShowTerms(false)}>
          <EmbedPdf
            src={showTerms === TERMS.APP ? appTerms && getCMSDocumentSrc(appTerms) : privacyPolicyTerms && getCMSDocumentSrc(privacyPolicyTerms)}
            downloadable
            height='50'
          />
          <Button className='mt-4' onClick={() => setShowTerms(false)}>{literals.common.close}</Button>
        </Modal>
      )}
    </div>
  );
};

ClientBasicInfo.propTypes = {
  literals: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onEvent: PropTypes.func.isRequired,
};

export default ClientBasicInfo;
